import { nanoid } from 'nanoid';
import { HttpError } from './HttpError.js'; // eslint-disable-line -- This is needed to make vue-tsc work in central-app :(

export type Query = Record<string, string | boolean | number | undefined>;
export type Headers = Record<string, string>;

type FetchFunction = typeof fetch;

type WindowWithAltFetch = Window & typeof globalThis & {
  [key: string]: FetchFunction | undefined;
};

interface HttpClientOptions {
  /** The base URL for the API, e.g., 'https://api.example.com'. */
  apiUrl : string;
  /** The name of an alternative fetch method to be used instead of the default fetch. */
  altFetch? : string;
  /** The access token set as `Bearer` token. */
  accessToken? : string;
  /** The trace ID for request tracing. */
  traceid?: string;
  /** Some default headers for all requests. */
  headers?: Headers;
}

export interface Options {
  // Which method to call on the response object for returning the actual data
  forceResponseMethod?: 'blob' | 'json' | 'text';
}

/** General HTTP client for requests to COMS APIs, i.e., api-consent and api-4p. */
export class HttpClient {
  apiUrl: string;
  accessToken?: string;
  altFetch?: string;
  clienttraceid: string;
  defaultHeaders: Headers;

  public constructor({
    apiUrl, altFetch, accessToken, traceid, headers = {},
  } : HttpClientOptions) {
    this.apiUrl = apiUrl;
    this.accessToken = accessToken;
    this.altFetch = altFetch;
    this.clienttraceid = traceid || nanoid();
    this.defaultHeaders = headers;
  }

  public async get<TResponse>(
    path: string,
    query: Query = {},
    headers: Headers = {},
  ): Promise<TResponse> {
    return this.fetch<undefined, TResponse>('GET', path, query, headers) as Promise<TResponse>;
  }

  public async post<TBody, TResponse = undefined>(
    path: string,
    body: TBody,
    query: Query = {},
    headers: Headers = {},
    options: Options = {},
  ): Promise<TResponse> {
    return this.fetch<TBody, TResponse>('POST', path, query, headers, body, options) as Promise<TResponse>;
  }

  public async fetch<TBody, TResponse>(
    method: 'POST' | 'GET',
    path: string,
    query: Query = {},
    headers: Headers = {},
    body?: TBody,
    options?: Options,
  ): Promise<TResponse | undefined> {
    const params = new URLSearchParams(
      Object.entries(query)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, `${value}`]),
    );

    const qs = params.toString();
    const qsPrefixed = qs ? `?${qs}` : '';
    const afetch: FetchFunction = this.altFetch
      ? (window as WindowWithAltFetch)[this.altFetch] ?? fetch
      : fetch;
    const slash = path.startsWith('/') || this.apiUrl.endsWith('/') ? '' : '/';
    const requesttraceid = nanoid(); // a new one for each single request

    const fetchRequestHeaders = {
      ...this.defaultHeaders,
      ...(method === 'GET' ? { Accept: 'application/json; charset=utf-8' } : {}),
      ...(method === 'POST' ? { 'Content-Type': 'application/json; charset=utf-8' } : {}),
      ...(this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {}),
      ...headers,
      'tef-coms-clienttraceid': this.clienttraceid,
      'tef-coms-traceid': requesttraceid,
      activityid: `${this.clienttraceid}|${requesttraceid}`,
    };

    const response = await afetch(`${this.apiUrl}${slash}${path}${qsPrefixed}`, {
      method,
      headers: fetchRequestHeaders,
      mode: 'cors',
      credentials: 'same-origin',
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    if (!response.ok) {
      const { status, statusText } = response;
      const errorBody = await response.text();
      throw new HttpError(status, statusText, errorBody);
    }

    if (options?.forceResponseMethod) {
      // eslint-disable-next-line
      return response[options.forceResponseMethod]();
    }

    // assuming only GETs have a body which is always JSON
    return method === 'GET' ? response.json() : undefined;
  }
}
